/** Boards. Spec 11.4: "CRUD boards and squares, arrow/snake maps, versioning, activation." */
import { createFileRoute, Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAdminContent } from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';

export const Route = createFileRoute('/$lang/admin/content/boards/')({ component: Page });

type BoardRow = {
  readonly id: string;
  readonly slug: string;
  readonly version: number;
  readonly name: string;
  readonly lastSquare: number;
  readonly isActive: boolean;
};

function Page(): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const { lang } = Route.useParams();
  const content = useAdminContent();

  const columns: readonly Column<BoardRow>[] = [
    {
      id: 'name',
      header: t('admin:content.boards'),
      cell: (row) => (
        <Link
          to="/$lang/admin/content/boards/$id"
          params={{ lang, id: row.id }}
          className="text-accent underline-offset-4 hover:underline"
        >
          {row.name}
        </Link>
      ),
    },
    { id: 'slug', header: 'slug', cell: (row) => `${row.slug} · v${String(row.version)}` },
    {
      id: 'squares',
      header: t('admin:content.squares'),
      align: 'end',
      cell: (row) => row.lastSquare,
    },
    {
      id: 'active',
      header: t('admin:content.active'),
      cell: (row) => (
        <Badge tone={row.isActive ? 'positive' : 'neutral'}>
          {row.isActive ? t('admin:content.active') : t('admin:content.draft')}
        </Badge>
      ),
    },
  ];

  if (content.error !== null) return <Alert tone="error">{t(errorKeyFor(content.error))}</Alert>;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-ink">{t('admin:nav.boards')}</h1>

      {content.isLoading ? (
        <SkeletonList rows={3} label={t('admin:nav.boards')} />
      ) : (
        <DataTable
          caption={t('admin:nav.boards')}
          columns={columns}
          rows={content.data?.boards ?? []}
          rowKey={(row) => row.id}
          empty={<EmptyState title={t('admin:content.draft')} />}
        />
      )}
    </div>
  );
}
